import { captureException } from '@sentry/browser';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import {
  AlgoliaCollectionHit,
  AlgoliaProductHit,
  LocalizedField,
  NormalizedCollectionHit,
  NormalizedProductHit,
  NormalizedProductHitOnClickCartOptions,
} from '../types';
import { formatMoney } from './money';

type AlgoliaUtilOptions = {
  appId: string;
  apiKey: string;
  hitsPerPage: {
    products: number;
    collections?: number;
    articles?: number;
  };
  indexNames: {
    product: string;
    collection?: string;
    article?: string;
  };
};

const domainToLanguageMap = new Map([
  ['uk', 'en'],
  ['com', 'en'],
  ['fr', 'fr'],
  ['nl', 'nl'],
  ['es', 'es'],
  ['de', 'de'],
  // ['test', 'en'],
]);

export const currentDomain =
  /* window.location.hostname === 'tameson-test.myshopify.com'
    ? 'test'
    : */ window?.location?.hostname?.split('.')?.pop()?.toLowerCase() ?? 'com';

export const currentLanguage = domainToLanguageMap.get(currentDomain) ?? 'en';

export const currentPriceAttribute = `price.${currentDomain}.${window.Shopify.currency.active}`;

export class AlgoliaUtil {
  private searchClient: SearchClient;

  constructor(private options: AlgoliaUtilOptions) {
    const { appId, apiKey } = options;

    this.searchClient = algoliasearch(appId, apiKey);
  }

  private getLocalized<T>(obj: LocalizedField<T>): T {
    if (obj && obj[currentLanguage]) {
      return obj[currentLanguage];
    }

    return obj as unknown as T;
  }

  public getInstantSearchParams = () => {
    return {
      searchClient: this.searchClient,
      hitsPerPage: this.options.hitsPerPage,
    };
  };

  public getIndexes = () => {
    return this.options.indexNames;
  };

  public normalizeProductHit = (hit: AlgoliaProductHit): NormalizedProductHit => {
    const leadTimeArray = hit.lead_time_array ?? [];

    const mpnValue = hit?.MPN || hit.meta?.product.MPN || '';

    const objectID = hit.ShopifyObjectID?.[currentDomain] ?? hit.objectID;

    const basePrice = this.getProductHitPrice(hit);

    const inventoryCount = hit.variants_inventory_count ?? hit['variants_inventory_count '] ?? 0;

    const ret = {
      title: this.getLocalized(hit.title),
      handle: this.getLocalized(hit.handle),
      sku: hit.sku,
      imageAltName: this.getLocalized(hit.title),
      availability: hit.availability,
      image: this.getProductOrCollectionHitImageURL(90, hit.image ?? hit.product_image),
      price: formatMoney(
        window.theme.featureFlags.showPricesInclVAT && window.theme.currentVATRate
          ? basePrice * Number(window.theme.currentVATRate)
          : basePrice
      ),
      basePrice,
      priceExclVAT:
        window.theme.featureFlags.showPricesInclVAT && window.theme.currentVATRate ? formatMoney(basePrice) : undefined,
      url: this.getProductHitURL(hit),
      onClickCartIcon: (options) => this.addProductHitToCart(event, { hitObjectID: objectID, ...options }),
      inventoryAvailable: hit.inventory_available,
      inventoryPolicy: hit.inventory_policy,
      variantsInventoryCount: inventoryCount,
      objectID,
      MPN: mpnValue,
      leadTimeArray,
      useUp: hit.UseUp,
    };

    return ret;
  };

  public normalizeCollectionHit = (hit: AlgoliaCollectionHit): NormalizedCollectionHit => {
    return {
      title: hit.title,
      imageAltName: hit.title,
      image: this.getProductOrCollectionHitImageURL(160, hit?.image),
      url: this.getCollectionHitURL(hit),
    };
  };

  private getProductHitURL = (hit: AlgoliaProductHit) => {
    return `${window.location.origin}/products/${this.getLocalized(hit.handle)}`;
  };

  private getCollectionHitURL = (hit: AlgoliaCollectionHit) => {
    return `${window.location.origin}/collections/${hit.handle}`;
  };

  private getProductOrCollectionHitImageURL = (width: number, imageURL?: LocalizedField) => {
    if (!imageURL) {
      return 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png?format=webp&v=1530129081';
    }

    const widthParam = `width=${width}`;
    let localizedImageURL: string;

    if (typeof imageURL === 'string') {
      localizedImageURL = imageURL;
    } else {
      localizedImageURL = this.getLocalized(imageURL);
    }

    return localizedImageURL + (localizedImageURL?.includes('?') ? `&${widthParam}` : `?${widthParam}`);
  };

  private getProductHitPrice = (hit: AlgoliaProductHit) => {
    const price = hit.price?.[currentDomain]?.[window.Shopify.currency.active] ?? hit.price;

    return Number(price);
  };

  private addProductHitToCart = async (
    event: any,
    options: NormalizedProductHitOnClickCartOptions & { hitObjectID: string }
  ) => {
    const addToCartButton = event.target.closest('button');
    addToCartButton.disabled = true;

    const cartNotification = document.querySelector('cart-notification');
    let sections = ['cart-icon-bubble'];
    const sectionsURL = window.location.pathname;

    // @ts-ignore
    if (cartNotification) {
      cartNotification.setActiveElement(document.activeElement);
      // @ts-ignore
      sections = cartNotification.getSectionsToRender().map((section) => section.id);
    }

    return (
      // @ts-ignore
      fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/javascript` },
        body: JSON.stringify({
          ...options,
          id: options.hitObjectID,
          sections,
          sections_url: sectionsURL,
        }),
      })
        .then((response) => response.json())
        .then((parsedState) => {
          // @ts-ignore
          if (cartNotification) {
            (cartNotification as any).renderContents(parsedState);
          } else {
            // @ts-ignore
            document.getElementById('cart-icon-bubble').innerHTML = parsedState.sections['cart-icon-bubble'];
          }
        })
        .catch((e) => captureException(e))
        .finally(() => {
          addToCartButton.disabled = false;
        })
    );
  };
}
